import type React from "react";
import {Handle, type NodeProps, Position} from "@xyflow/react";
import {alpha, Stack, Tooltip, Typography, useTheme} from "@mui/material";
import {PART_SEP} from "~/constants";
import {TAXONOMY_CHANGE_META} from "~/pages/taxonomy-builder-v2/constants/taxonomyChangeMeta";
import {type DiffNode, MERGE_HANDLE} from "./taxonomyChangeDiffGraph";
import {Tag} from "~/components/tag/Tag";

const HIDDEN_HANDLE: React.CSSProperties = {opacity: 0, pointerEvents: 'none'};

/**
 * A category in the change picture. The changed one takes the change's colour; deleted ones are faded
 * with a dashed border; a renamed one shows its new name in bold over the old one struck through; a
 * category merged into the changed one has a dashed border in the change's colour. Each change on a card
 * is its own tag (e.g. "moved from …"), cut off with an ellipsis when long; hovering the tag shows it in full.
 */
export const ChangeDiffNodeCard: React.FC<NodeProps<DiffNode>> = ({data}) => {
    const theme = useTheme();
    const palette = TAXONOMY_CHANGE_META[data.type].palette;
    const color = palette ? theme.palette[palette].main : theme.palette.grey[600];
    const isFocus = data.role === 'focus';
    const isMore = data.role === 'more';
    const isOriginal = data.role === 'original';
    // One tag per change, e.g. "moved from "Foils"" and "merged into "Glass"", or the level when unchanged.
    const sublines = data.note ? data.note.split(PART_SEP) : [`L${data.level}`];

    return (
        <Stack
            justifyContent="center"
            gap={0.25}
            sx={{
                height: '100%',
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                border: isFocus ? 2 : 1,
                // Dashed means "before" (deleted): "+N more" is a plain summary, so it stays solid and grey.
                borderStyle: (data.removed && !isMore) || isOriginal ? 'dashed' : 'solid',
                borderColor: isFocus || isOriginal ? color : isMore ? 'grey.200' : 'mithraBlue.500',
                bgcolor: isFocus ? alpha(color, 0.16) : isMore ? 'grey.50' : 'background.paper',
                color: isMore ? 'text.secondary' : 'mithraBlue.800',
                opacity: data.removed ? 0.45 : 1,
                boxShadow: isFocus && !data.removed ? 2 : 0,
            }}
        >
            <Handle type="target" position={Position.Left} isConnectable={false} style={HIDDEN_HANDLE}/>
            {/* Names wrap instead of being cut off: the card is sized to fit them in full. */}
            <Typography
                fontSize={13.5}
                lineHeight={1.3}
                fontWeight={isFocus || data.prevLabel ? 700 : 500}
                fontStyle={isMore ? 'italic' : undefined}
                sx={{overflowWrap: 'anywhere'}}
            >
                {data.label}
            </Typography>
            {data.prevLabel && (
                <Typography fontSize={12} lineHeight={1.25}
                            sx={{textDecoration: 'line-through', opacity: 0.75, overflowWrap: 'anywhere'}}>
                    {data.prevLabel}
                </Typography>
            )}
            {!isMore && sublines.map(subline => {
                const tag = (
                    <Tag
                        label={subline}
                        color="default"
                        variant="outlined"
                        size="small"
                        sx={{maxWidth: '100%', minHeight: '14px', '&&': {borderRadius: 0.5}}}
                    />
                );
                // The level of an unchanged card needs no tooltip.
                return data.note
                    ? (
                        <Tooltip key={subline} title={subline} placement="top" disableInteractive>
                            {/* Tag doesn't forward a ref, which the tooltip needs. The read-only canvas turns
                                pointer events off on its nodes: the tag takes them back for the hover. */}
                            <Stack component="span" sx={{alignSelf: 'flex-start', maxWidth: '100%', minWidth: 0, pointerEvents: 'auto'}}>
                                {tag}
                            </Stack>
                        </Tooltip>
                    )
                    : <Stack key={subline} component="span" sx={{alignSelf: 'flex-start', maxWidth: '100%', minWidth: 0}}>{tag}</Stack>;
            })}
            <Handle type="source" position={Position.Right} isConnectable={false} style={HIDDEN_HANDLE}/>
            {isOriginal && (
                <Handle id={MERGE_HANDLE} type="source" position={Position.Left} isConnectable={false} style={HIDDEN_HANDLE}/>
            )}
        </Stack>
    );
};
